import { SyncOutlined } from "@ant-design/icons";
import { Button, Flex, Typography } from "antd";
import dayjs from "dayjs";
import { useAppSelector } from "../../hooks/redux-hooks";
import type { EHRSyncRun } from "../../types/index.type";
import { useEhrSync } from "./useEhrSync";

const resultLine = (run: EHRSyncRun | null): string => {
  if (!run) return "";
  if (!run.created && !run.updated) return "Already up to date";
  return `${run.created} added, ${run.updated} updated`;
};

const countdown = (seconds: number): string => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

/**
 * Pulls today's patients from the EHR on demand, for the clinicians whose credential
 * is routed through the fast bundle path. `can_sync_ehr_patients` is computed once on
 * bootstrap and is the ONLY thing that reveals the button — eligibility costs a flag
 * read, a group load and a release query on the server, so it is never re-derived here.
 */
const EhrSyncButton = () => {
  const canSync = useAppSelector((state) => state.auth.userInfo.can_sync_ehr_patients);
  const { phase, run, lastSyncedAt, cooldownRemainingS, startSync } = useEhrSync();

  if (!canSync) return null;

  const isRunning = phase === "running";
  const isFailure = phase === "busy" || phase === "refused" || phase === "error";

  // A cold credential spends ~50s on the eCW login alone, so the wait is stated in
  // minutes. Promising seconds here would be a lie on the majority of clicks.
  const caption = (() => {
    switch (phase) {
      case "running":
        return "usually under a minute";
      case "ok":
        return resultLine(run);
      case "busy":
        return "Another sync for your clinic is running — try again in a minute.";
      case "refused":
        return "Sync isn't enabled for your clinic.";
      case "error":
        return "Couldn't reach eCW — try again shortly.";
      case "cooldown":
        return `Available again in ${countdown(cooldownRemainingS)}`;
      default:
        return lastSyncedAt ? `Last synced ${dayjs(lastSyncedAt).format("h:mm A")}` : "";
    }
  })();

  return (
    <Flex align="center" gap={8} style={{ padding: "0 2px" }}>
      <Button
        size="small"
        icon={<SyncOutlined spin={isRunning} />}
        onClick={startSync}
        disabled={isRunning || phase === "cooldown"}
      >
        {isRunning ? "Syncing…" : "Sync"}
      </Button>
      {caption && (
        <Typography.Text
          aria-live="polite"
          type={isFailure ? "danger" : "secondary"}
          style={{ fontSize: 12, lineHeight: "16px" }}
        >
          {caption}
        </Typography.Text>
      )}
    </Flex>
  );
};

export default EhrSyncButton;
